import { Controller } from "@hotwired/stimulus"

// Sub Payments tab (index): new-contract modal (fee-schedule vs as-needed
// type, required-docs checklist, company sub database type-to-search),
// buyout rows (add / link to a sub contract / delete), row navigation.
export default class extends Controller {
  static targets = ["newModal", "createBtn", "buyoutModal", "buyoutBody", "buyoutTable", "buyoutEmpty",
                    "typeToggle", "amountWrap", "retainageWrap", "subResults", "contractsBody", "accessWrap", "accessPerms",
                    "invoiceWrap", "invoiceZone", "invoiceInput", "invoiceChips", "invoicePeekStatus",
                    "docWrap", "docZone", "docInput", "docChips", "docPeekStatus",
                    "dupModal", "dupNewName", "dupExistingName", "dupExistingMeta"]
  static values = { createUrl: String, buyoutUrl: String, searchUrl: String, dupCheckUrl: String,
                    presignUrl: String, readUrl: String, readDocUrl: String }

  connect() {
    this._invoiceFiles = []
    this._docFiles = []
    const msg = sessionStorage.getItem("builder-finance.flash")
    if (msg) {
      sessionStorage.removeItem("builder-finance.flash")
      this._showToast(msg)
    }
  }

  openContract(e) {
    if (e.target.closest("button, a, input, select")) return
    window.location.href = e.currentTarget.dataset.url
  }

  openNewModal() { this.newModalTarget.classList.remove("hidden") }
  addBuyoutRow() { this.buyoutModalTarget.classList.remove("hidden") }
  closeModals() {
    this.newModalTarget.classList.add("hidden")
    if (this.hasBuyoutModalTarget) this.buyoutModalTarget.classList.add("hidden")
  }
  stopPropagation(e) { e.stopPropagation() }

  // ── Sub type (Larry 2026-07-23): fee-schedule vs as-needed ──────────────
  // As-needed subs have no contract amount or retainage, so those fields hide.
  pickType(e) {
    const type = e.currentTarget.dataset.paymentType
    this.newModalTarget.querySelector('[data-field="payment_type"]').value = type
    this.typeToggleTarget.querySelectorAll("[data-payment-type]").forEach((btn) => {
      const active = btn.dataset.paymentType === type
      btn.classList.toggle("border-brand", active)
      btn.classList.toggle("bg-brand/5", active)
      btn.classList.toggle("border-gray-200", !active)
    })
    const asNeeded = type === "as_needed"
    this.amountWrapTarget.classList.toggle("hidden", asNeeded)
    this.retainageWrapTarget.classList.toggle("hidden", asNeeded)
    if (this.hasAccessWrapTarget) this.accessWrapTarget.classList.toggle("hidden", asNeeded)
    // As-needed extras (Larry 2026-08-26): the invoice dropzone appears,
    // and the button says what it really does — create a sub payment.
    if (this.hasInvoiceWrapTarget) this.invoiceWrapTarget.classList.toggle("hidden", !asNeeded)
    // Fee-schedule papers drop (S3-8) — the other side of the same spot.
    if (this.hasDocWrapTarget) this.docWrapTarget.classList.toggle("hidden", asNeeded)
    // Portal invite default follows the type (Phelipe 2026-08-31): the
    // portal is how a fee-schedule sub bills, but an as-needed add is often
    // just recording an invoice — no email unless the GC asks for one.
    const invite = this.newModalTarget.querySelector("[data-portal-invite]")
    if (invite) invite.checked = !asNeeded
    // Required-doc defaults follow the type too (Phelipe 2026-08-31, from
    // Larry's Strata add): a one-off doesn't need a COI chase, the W-9
    // stays — it's a tax form either way. Fee-schedule keeps COI + W-9.
    this.newModalTarget.querySelectorAll("[data-required-doc]").forEach((el) => {
      // As-needed subs start with NOTHING required — "leave them blank and we
      // can choose" (Larry item 17, 2026-09-01). Fee-schedule keeps its defaults.
      el.checked = asNeeded ? false : el.defaultChecked
    })
    this._setCreateLabel(asNeeded)
  }

  _setCreateLabel(asNeeded) {
    const btn = this.createBtnTarget
    const label = asNeeded ? btn.dataset.labelPayment : btn.dataset.labelContract
    if (label) btn.textContent = label
  }

  accessModeChanged(e) {
    if (this.hasAccessPermsTarget) this.accessPermsTarget.classList.toggle("hidden", e.target.value !== "full")
  }

  // ── As-needed invoice dropzone (Larry 2026-08-26) ────────────────────────
  // Files presign → PUT to S3 as chips while the GC finishes the form; the
  // create request carries {cache_name, filename} pairs and the server files
  // them on a draft invoice under the new sub.
  browseInvoice() { this.invoiceInputTarget.click() }

  invoiceFilesChosen(e) {
    Array.from(e.target.files || []).forEach((f) => this._uploadInvoiceOne(f))
    e.target.value = ""
  }

  dragOverInvoice(e) {
    e.preventDefault()
    this.invoiceZoneTarget.classList.add("border-purple-500", "bg-purple-100")
  }

  dragLeaveInvoice() {
    this.invoiceZoneTarget.classList.remove("border-purple-500", "bg-purple-100")
  }

  dropInvoice(e) {
    e.preventDefault()
    this.dragLeaveInvoice()
    Array.from(e.dataTransfer?.files || []).forEach((f) => this._uploadInvoiceOne(f))
  }

  _uploadInvoiceOne(file) {
    // First file of the batch: AI reads it and prefills the form below
    // (Phelipe 2026-08-27) — vendor → name, specifics → scope; the
    // invoice number/date/amount ride the entry onto the draft invoice.
    return this._uploadChip(file, this.invoiceChipsTarget, this._invoiceFiles,
      (entry) => { if (this._invoiceFiles.length === 1) this._peekInvoice(entry) })
  }

  // One chip per dropped file (app convention): blue + spinner while the
  // presign → S3 PUT runs, gray + × when done. `list` is the array the
  // create request reads; `onUploaded(entry, chip)` runs once the file is up.
  async _uploadChip(file, chips, list, onUploaded) {
    const esc = (s) => (s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
    const chip = document.createElement("span")
    chip.className = "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 border border-blue-200 text-[13px] text-blue-700 max-w-full"
    chip.innerHTML = `<svg class="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg><span class="truncate max-w-[220px]">${esc(file.name)}</span>`
    chips.appendChild(chip)

    try {
      const presignResp = await fetch(this.presignUrlValue, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size }] })
      })
      const presign = await presignResp.json()
      if (!presignResp.ok || !presign.uploads?.[0]) throw new Error(presign.error || "presign failed")

      const { upload_url, cache_name } = presign.uploads[0]
      const put = await fetch(upload_url, { method: "PUT", body: file })
      if (!put.ok) throw new Error(`S3 ${put.status}`)

      const entry = { cache_name, filename: file.name }
      list.push(entry)
      chip.className = "inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full bg-gray-100 border border-gray-200 text-[13px] text-gray-700 max-w-full"
      chip.innerHTML = `<svg class="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"/></svg><span class="truncate max-w-[220px]">${esc(file.name)}</span>`
      const x = document.createElement("button")
      x.type = "button"
      x.className = "text-gray-400 hover:text-red-500 font-bold leading-none cursor-pointer"
      x.textContent = "×"
      x.addEventListener("click", () => {
        const i = list.indexOf(entry)
        if (i >= 0) list.splice(i, 1)
        chip.remove()
      })
      chip.appendChild(x)
      if (onUploaded) onUploaded(entry, chip)
    } catch (err) {
      chip.remove()
      this._showToast(err.message || "Upload failed — please try again.", "alert")
    }
  }

  // ── Fee-schedule papers dropzone (S3-8, Jason/Starr 2026-10-21) ─────────
  // The sub's proposal or signed contract. Every file is read (one may be
  // the proposal, another the contract); the create request carries the
  // entries with what the AI read — lines → fee schedule, kind "contract"
  // → filed on the Contracts page, linked to the sub.
  browseDoc() { this.docInputTarget.click() }

  docFilesChosen(e) {
    Array.from(e.target.files || []).forEach((f) => this._uploadDocOne(f))
    e.target.value = ""
  }

  dragOverDoc(e) {
    e.preventDefault()
    this.docZoneTarget.classList.add("border-purple-500", "bg-purple-100")
  }

  dragLeaveDoc() {
    this.docZoneTarget.classList.remove("border-purple-500", "bg-purple-100")
  }

  dropDoc(e) {
    e.preventDefault()
    this.dragLeaveDoc()
    Array.from(e.dataTransfer?.files || []).forEach((f) => this._uploadDocOne(f))
  }

  _uploadDocOne(file) {
    return this._uploadChip(file, this.docChipsTarget, this._docFiles, (entry, chip) => this._peekDocument(entry, chip))
  }

  async _peekDocument(entry, chip) {
    if (!this.hasReadDocUrlValue || !this.readDocUrlValue) return
    const status = this.hasDocPeekStatusTarget ? this.docPeekStatusTarget : null
    if (status) this._setPeekStatus(status, "reading")
    try {
      const resp = await fetch(this.readDocUrlValue, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ cache_name: entry.cache_name, filename: entry.filename })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "read failed")

      entry.kind = data.kind || "other"
      entry.title = data.title || null
      entry.amount = data.amount || null
      entry.executed_on = data.executed_on || null
      entry.scope = data.scope || null
      entry.line_items = Array.isArray(data.line_items) ? data.line_items : []

      let filled = false
      const fill = (field, value) => { if (this._fillUntouched(field, value)) filled = true }
      fill("name", data.vendor)
      fill("trade", data.trade)
      fill("contact_name", data.contact_name)
      fill("contact_email", data.contact_email)
      fill("scope", data.scope)
      // A priced breakdown becomes the fee schedule (which sets the value);
      // a plain lump sum fills the amount box instead.
      if (data.amount && entry.line_items.length === 0) fill("amount", String(data.amount))
      // Tag the chip with what the paper turned out to be.
      if (chip && status && entry.kind !== "other") {
        const tag = document.createElement("span")
        tag.className = "px-1.5 rounded-full bg-purple-100 text-purple-700 text-[13px]"
        tag.textContent = entry.kind === "contract" ? status.dataset.kindContractText : status.dataset.kindProposalText
        chip.insertBefore(tag, chip.lastElementChild)
      }
      if (status) this._setPeekStatus(status, filled ? "filled" : null)
    } catch (_) {
      if (status) this._setPeekStatus(status, null)
    }
  }

  // Prefill never clobbers what the GC already typed (Phelipe 2026-08-27).
  _fillUntouched(field, value) {
    if (!value) return false
    const el = this.newModalTarget.querySelector(`[data-field="${field}"]`)
    if (!el) return false
    const untouched = !el.value.trim() || el.value === el.dataset.autofilled
    if (!untouched) return false
    el.value = value
    el.dataset.autofilled = value
    return true
  }

  // ── Company sub database (Larry/Phelipe 2026-07-23) ─────────────────────
  // Type-to-search subs from the company's other projects; picking one fills
  // the form and carries their compliance paperwork over.
  searchSubs(e) {
    clearTimeout(this._searchTimer)
    const q = e.target.value.trim()
    this.newModalTarget.querySelector('[data-field="copy_from_id"]').value = ""
    if (q.length < 2) return this.subResultsTarget.classList.add("hidden")
    this._searchTimer = setTimeout(() => this._runSubSearch(q), 250)
  }

  async _runSubSearch(q) {
    try {
      const resp = await fetch(`${this.searchUrlValue}?q=${encodeURIComponent(q)}`, { headers: { Accept: "application/json" } })
      if (!resp.ok) return
      const data = await resp.json()
      const esc = (s) => (s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
      if (!data.subs.length) return this.subResultsTarget.classList.add("hidden")
      this.subResultsTarget.innerHTML = data.subs.map((s) => {
        const good = s.docs.filter((d) => d.status === "valid").map((d) => d.label)
        const bad = s.docs.filter((d) => ["expired", "expiring", "missing"].includes(d.status)).map((d) => `${d.label} ${d.status}`)
        const docs = [
          good.length ? `<span class="text-green-600">${esc(good.join(" · "))} on file</span>` : "",
          bad.length ? `<span class="text-amber-600">${esc(bad.join(" · "))}</span>` : ""
        ].filter(Boolean).join(" · ")
        return `<button type="button" data-action="click->sub-payments#chooseSub" data-sub='${esc(JSON.stringify(s))}'
                  class="block w-full text-left px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0">
          <span class="block text-[13px] font-medium text-gray-800">${esc(s.name)}${s.trade ? ` <span class="font-normal text-gray-400">· ${esc(s.trade)}</span>` : ""}</span>
          <span class="block text-[13px] text-gray-500">Used on ${esc(s.project || "another project")}${docs ? ` · ${docs}` : ""}</span>
        </button>`
      }).join("")
      this.subResultsTarget.classList.remove("hidden")
    } catch { /* type-ahead is best-effort */ }
  }

  chooseSub(e) {
    const s = JSON.parse(e.currentTarget.dataset.sub)
    const set = (f, v) => { const el = this.newModalTarget.querySelector(`[data-field="${f}"]`); if (el && v) el.value = v }
    set("name", s.name)
    this._autofillTrade(s.trade)
    set("contact_name", s.contact_name); set("contact_email", s.contact_email)
    this.newModalTarget.querySelector('[data-field="copy_from_id"]').value = s.id || ""
    this.subResultsTarget.classList.add("hidden")
    this._showToast(s.id ? `${s.name} found in your company list — their paperwork carries over.` : `${s.name} linked from your subcontractor list.`)
  }

  // Item 17: the picked sub's known trade fills in — and switching picks
  // replaces the previous pick's autofill (tracked via dataset.autofilled) —
  // but text the GC typed themselves is never clobbered.
  _autofillTrade(trade) {
    const el = this.newModalTarget.querySelector('[data-field="trade"]')
    if (!el) return
    const untouched = !el.value.trim() || el.value === el.dataset.autofilled
    if (!untouched) return
    el.value = trade || ""
    if (trade) el.dataset.autofilled = trade
    else delete el.dataset.autofilled
  }

  hideSubResultsSoon() { setTimeout(() => this.subResultsTarget.classList.add("hidden"), 200) }

  // Stays on the list after adding (Phelipe 2026-07-23): the new row slots in
  // alphabetically; the GC clicks it for details. First-ever sub has no table
  // to slot into (empty state), so that one reloads.
  // Custom required-document names (Phelipe 2026-07-25): typed → checked
  // chip in the checklist; unchecked ones don't submit.
  addCustomDoc(e) {
    const wrap = e.currentTarget.closest("[data-custom-doc-wrap]")
    const input = wrap.querySelector("[data-custom-doc-input]")
    const name = (input.value || "").trim()
    if (!name) return
    input.value = ""
    const esc = (s) => (s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
    wrap.insertAdjacentHTML("beforebegin",
      `<label class="inline-flex items-center gap-2 text-[13px] text-gray-700 cursor-pointer"><input type="checkbox" checked data-custom-doc="${esc(name)}" class="rounded border-gray-300 text-brand focus:ring-brand">${esc(name)}</label>`)
  }

  async createContract() {
    const body = {}
    this.newModalTarget.querySelectorAll("[data-field]").forEach((el) => { body[el.dataset.field] = el.value })
    // Checkboxes/radios carry state, not .value (Phelipe 2026-08-31).
    body.send_portal_invite = this.newModalTarget.querySelector("[data-portal-invite]")?.checked ? "1" : "0"
    body.invoice_status = this.newModalTarget.querySelector("[data-invoice-status]:checked")?.value || "received"
    body.required_docs = [...this.newModalTarget.querySelectorAll("[data-required-doc]:checked")].map((el) => el.dataset.requiredDoc)
    body.access_perms = [...this.newModalTarget.querySelectorAll("[data-access-perm]:checked")].map((el) => el.dataset.accessPerm)
    body.custom_docs = [...this.newModalTarget.querySelectorAll("[data-custom-doc]:checked")].map((el) => el.dataset.customDoc)
    if (!body.name) return this._showToast("Give the sub a name.", "alert")

    // Duplicate gate (Phelipe 2026-08-19): before creating, ask the server —
    // loose match + one AI pass — whether this sub already exists in the
    // company directory. On a hit the GC picks: reuse it, or truly new.
    // A picking-a-search-result flow already carries copy_from_id — that IS
    // an existing sub, skip the check. The check failing skips silently.
    if (!body.vendor_company_id && !body.force_new_vendor && !body.copy_from_id && this.hasDupCheckUrlValue) {
      this.createBtnTarget.disabled = true
      let dup = null
      try {
        const resp = await fetch(this.dupCheckUrlValue, { method: "POST", headers: this._headers(),
          body: JSON.stringify({ name: body.name, contact_email: body.contact_email }) })
        dup = (await resp.json())?.duplicate
      } catch { dup = null }
      if (dup && this.hasDupModalTarget) {
        this._pendingCreate = body
        this._dupId = dup.id
        this.dupNewNameTarget.textContent = body.name
        this.dupExistingNameTarget.textContent = dup.name
        const bits = []
        bits.push(`${dup.projects || 0} project${dup.projects === 1 ? "" : "s"}`)
        if (dup.docs > 0) bits.push(`${dup.docs} document${dup.docs === 1 ? "" : "s"} on file`)
        if (dup.contact) bits.push(dup.contact)
        this.dupExistingMetaTarget.textContent = bits.join(" · ")
        this.createBtnTarget.disabled = false
        this.dupModalTarget.classList.remove("hidden")
        return
      }
    }
    await this._reallyCreate(body)
  }

  // Duplicate modal choices — reuse the directory company, or force a new one.
  dupUseExisting() {
    const body = this._pendingCreate || {}
    body.vendor_company_id = this._dupId
    this.closeDupModal()
    this._reallyCreate(body)
  }

  dupCreateNew() {
    const body = this._pendingCreate || {}
    body.force_new_vendor = "1"
    this.closeDupModal()
    this._reallyCreate(body)
  }

  closeDupModal() {
    if (this.hasDupModalTarget) this.dupModalTarget.classList.add("hidden")
  }

  // AI read of the dropped invoice (Phelipe 2026-08-27) — reuses the
  // Internal Expenses receipt reader. Prefill never clobbers what the GC
  // already typed; failures stay quiet.
  async _peekInvoice(entry) {
    if (!this.hasReadUrlValue || !this.readUrlValue) return
    this._setInvoicePeekStatus("reading")
    try {
      const resp = await fetch(this.readUrlValue, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ cache_name: entry.cache_name, filename: entry.filename })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "read failed")

      entry.number = data.invoice_number || null
      entry.invoice_date = data.invoice_date || null
      entry.amount = data.amount || null
      // Ride the invoice's own breakdown into the create — the as-needed
      // contract turns these into fee-schedule lines so the pay app has
      // real rows to bill against (Phelipe 2026-08-27).
      entry.description = data.description || null
      entry.line_items = Array.isArray(data.line_items) ? data.line_items : []

      let filled = false
      const fill = (field, value) => { if (this._fillUntouched(field, value)) filled = true }
      fill("name", data.vendor)
      fill("scope", data.description)
      // Phelipe 2026-08-27: the reader also pulls these when the invoice
      // shows them — same untouched-field rule, so a sub picked from the
      // company database keeps its own contact info.
      fill("trade", data.trade)
      fill("contact_name", data.contact_name)
      fill("contact_email", data.contact_email)
      this._setInvoicePeekStatus(filled ? "filled" : null)
    } catch (_) {
      this._setInvoicePeekStatus(null)
    }
  }

  _setInvoicePeekStatus(state) {
    if (!this.hasInvoicePeekStatusTarget) return
    this._setPeekStatus(this.invoicePeekStatusTarget, state)
  }

  _setPeekStatus(el, state) {
    if (!state) return el.classList.add("hidden")
    el.textContent = state === "reading" ? el.dataset.readingText : el.dataset.filledText
    el.classList.remove("hidden")
    if (state === "filled") setTimeout(() => el.classList.add("hidden"), 6000)
  }

  async _reallyCreate(body) {
    if (this._invoiceFiles.length) body.invoice_files = this._invoiceFiles
    if (this._docFiles.length && body.payment_type !== "as_needed") body.documents = this._docFiles
    this.createBtnTarget.disabled = true
    try {
      const resp = await fetch(this.createUrlValue, { method: "POST", headers: this._headers(), body: JSON.stringify(body) })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not create the contract")
      // Smart match (Larry 2026-09-01): the server filed it under a sub that
      // already exists on the project — nothing new to add to the list.
      if (data.existing) {
        this.closeModals()
        this._resetNewModal()
        this._showToast(`Filed under the existing sub "${data.name}" — click its row for details`)
        return
      }
      if (!this.hasContractsBodyTarget) {
        sessionStorage.setItem("builder-finance.flash", `${data.name} added`)
        return window.location.reload()
      }
      const rows = [...this.contractsBodyTarget.querySelectorAll("tr")]
      const after = rows.find((r) => (r.querySelector("td")?.textContent || "").trim().localeCompare(data.name, undefined, { sensitivity: "base" }) > 0)
      if (after) after.insertAdjacentHTML("beforebegin", data.row_html)
      else this.contractsBodyTarget.insertAdjacentHTML("beforeend", data.row_html)
      this.closeModals()
      this._resetNewModal()
      this._showToast(`${data.name} added — click the row for details`)
    } catch (err) {
      this._showToast(err.message, "alert")
    } finally {
      this.createBtnTarget.disabled = false
    }
  }

  _resetNewModal() {
    this.newModalTarget.querySelectorAll("[data-field]").forEach((el) => {
      el.value = el.dataset.field === "payment_type" ? "fee_schedule" : (el.dataset.field === "retainage_pct" ? "0" : "")
      delete el.dataset.autofilled
    })
    // Back to the form's defaults: COI + W-9 render checked, the rest not.
    this.newModalTarget.querySelectorAll("[data-required-doc]").forEach((el) => { el.checked = el.defaultChecked })
    const invite = this.newModalTarget.querySelector("[data-portal-invite]")
    if (invite) invite.checked = true
    const review = this.newModalTarget.querySelector('[data-invoice-status][value="received"]')
    if (review) review.checked = true
    this.newModalTarget.querySelectorAll("[data-custom-doc]").forEach((el) => el.closest("label")?.remove())
    this.typeToggleTarget.querySelectorAll("[data-payment-type]").forEach((btn) => {
      const active = btn.dataset.paymentType === "fee_schedule"
      btn.classList.toggle("border-brand", active)
      btn.classList.toggle("bg-brand/5", active)
      btn.classList.toggle("border-gray-200", !active)
    })
    this.amountWrapTarget.classList.remove("hidden")
    this.retainageWrapTarget.classList.remove("hidden")
    // Fee-schedule is the reset default, so its sections come back and the
    // as-needed extras clear (the old reset left accessWrap hidden and the
    // toggle half-stale — the end-to-end bug Larry hit on 2026-08-26).
    if (this.hasAccessWrapTarget) this.accessWrapTarget.classList.remove("hidden")
    if (this.hasInvoiceWrapTarget) this.invoiceWrapTarget.classList.add("hidden")
    if (this.hasInvoiceChipsTarget) this.invoiceChipsTarget.innerHTML = ""
    if (this.hasInvoicePeekStatusTarget) this.invoicePeekStatusTarget.classList.add("hidden")
    this._invoiceFiles = []
    if (this.hasDocWrapTarget) this.docWrapTarget.classList.remove("hidden")
    if (this.hasDocChipsTarget) this.docChipsTarget.innerHTML = ""
    if (this.hasDocPeekStatusTarget) this.docPeekStatusTarget.classList.add("hidden")
    this._docFiles = []
    this._setCreateLabel(false)
    this.subResultsTarget.classList.add("hidden")
  }

  async createBuyout() {
    const body = {}
    this.buyoutModalTarget.querySelectorAll("[data-buyout-field]").forEach((el) => { body[el.dataset.buyoutField] = el.value })
    if (!body.trade) return this._showToast("Name the trade.", "alert")
    const resp = await fetch(this.buyoutUrlValue, { method: "POST", headers: this._headers(), body: JSON.stringify(body) })
    const data = await resp.json()
    if (!resp.ok) return this._showToast(data.error || "Could not add the trade", "alert")
    sessionStorage.setItem("builder-finance.flash", "Trade added to the buyout board")
    window.location.reload()
  }

  async linkBuyout(e) {
    const id = e.currentTarget.dataset.buyoutId
    const resp = await fetch(`${this.buyoutUrlValue}/${id}`, {
      method: "PATCH", headers: this._headers(),
      body: JSON.stringify({ sub_contract_id: e.currentTarget.value })
    })
    if (resp.ok) window.location.reload()
    else this._showToast("Could not link the trade", "alert")
  }

  async deleteBuyout(e) {
    const row = e.currentTarget.closest("tr")
    const resp = await fetch(`${this.buyoutUrlValue}/${row.dataset.buyoutId}`, { method: "DELETE", headers: this._headers() })
    if (resp.ok) row.remove()
  }

  _headers() {
    return {
      "Content-Type": "application/json", "Accept": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
    }
  }

  _showToast(message, type = "notice") {
    const div = document.createElement("div")
    div.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    div.setAttribute("data-controller", "flash")
    div.setAttribute("data-flash-dismiss-after-value", "5000")
    div.textContent = message
    document.body.prepend(div)
  }
}
